import * as THREE from 'three';
import { Inventory } from './inventory';
import { terrainHeight } from '../engine/terrain';
import { applyInteriorDark } from '../engine/interior';

// A SATCHEL BAG: the drop a body leaves when it is CHOPPED apart (the 3rd melee hit destroys the body and
// the remaining carry falls into a small sack on the ground). It opens as a temporary chest (E) and expires
// with the standard 10-minute ground-drop TTL, blinking out its last seconds, so a battlefield does not
// litter forever. Kept deliberately small + separate from the cyan node-loot box so the "sack" reads at a
// glance in the dropped-item visual language. Client-only (both SP corpse-chop and MP sleeper-chop feed it).

const TTL_SEC = 600;   // 10 minutes on the ground, matching DroppedItemField
const BLINK_SEC = 8;   // warning blink at the end of life

export class Satchel {
  readonly mesh: THREE.Group;
  readonly pos: THREE.Vector3;
  readonly contents: Inventory;
  age = 0;

  constructor(pos: THREE.Vector3, contents: Inventory) {
    this.pos = pos.clone();
    this.contents = contents;
    this.mesh = buildSackMesh();
    this.mesh.position.set(this.pos.x, terrainHeight(this.pos.x, this.pos.z) + 0.2, this.pos.z);
    this.mesh.traverse((o) => { o.userData.satchel = this; }); // backref for aim-targeting
  }
}

export class SatchelField {
  readonly group = new THREE.Group();
  private readonly list: Satchel[] = [];

  // Spawn a satchel at (x,z) holding `items`. Empty piles are skipped (a chopped body with nothing left
  // leaves no bag). Returns the satchel, or null when there was nothing to hold.
  spawn(x: number, z: number, items: Array<{ id: import('./inventory').ItemId; amount: number }>): Satchel | null {
    const real = items.filter((it) => it.amount > 0);
    if (!real.length) return null;
    const inv = new Inventory(24); // roomy: a full body carry collapses into stacks here
    for (const it of real) inv.add(it.id, it.amount);
    const s = new Satchel(new THREE.Vector3(x, 0, z), inv);
    this.list.push(s);
    this.group.add(s.mesh);
    return s;
  }

  // The satchel under the cursor ray (for the E prompt), or null.
  aimed(raycaster: THREE.Raycaster): Satchel | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.satchel) return o.userData.satchel as Satchel; o = o.parent; }
    }
    return null;
  }

  remove(s: Satchel): void {
    const i = this.list.indexOf(s);
    if (i < 0) return;
    this.list.splice(i, 1);
    this.group.remove(s.mesh);
  }

  // Expire satchels older than the TTL (and blink the last seconds). Call each frame with dt in seconds.
  update(dt: number, time: number): void {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const s = this.list[i];
      s.age += dt;
      if (s.age >= TTL_SEC || s.contents.isEmpty()) { this.remove(s); continue; } // gone (expired or fully looted)
      const left = TTL_SEC - s.age;
      s.mesh.visible = left > BLINK_SEC || Math.floor(time * 5) % 2 === 0;
    }
  }
}

// A small burlap sack: a squat rounded body cinched at a knotted neck. Flat-shaded, interior-dimmed like
// the other ground props so it reads in a sealed base.
function buildSackMesh(): THREE.Group {
  const g = new THREE.Group();
  const cloth = new THREE.MeshLambertMaterial({ color: 0x8a6a3a, flatShading: true }); applyInteriorDark(cloth);
  const tie = new THREE.MeshLambertMaterial({ color: 0x5a4426, flatShading: true }); applyInteriorDark(tie);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.26, 8, 6), cloth);
  body.scale.set(1, 0.85, 1);
  body.position.y = 0.24;
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.13, 0.14, 6), cloth);
  neck.position.y = 0.44;
  const knot = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.09, 0.05, 6), tie);
  knot.position.y = 0.5;
  for (const m of [body, neck, knot]) { m.castShadow = true; m.receiveShadow = true; }
  g.add(body, neck, knot);
  return g;
}
